import { useState, useRef, useMemo, useCallback } from "react";
import api from "../../../lib/api";
import { 
  FileText, Upload, Download, Eye, RefreshCw, 
  Loader2, CheckCircle2, AlertTriangle, History,
  ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { 
  Collapsible, CollapsibleContent, CollapsibleTrigger 
} from "../../ui/collapsible";
import { toast } from "sonner";
import { DocumentCopilotChat } from "../../DocumentCopilotChat";
import { useOrganizationScope } from "../../../contexts/OrganizationScopeContext";

export const DocumentGenerationCard = ({
  missionId,
  mission,
  optimizationData,
  knowledgeBase,
  previousGenerations,
  generatingDoc,
  setGeneratingDoc,
  generatedDoc,
  setGeneratedDoc,
  onShowPreview,
  onRefreshGenerations,
  user,
  language = "cs"
}) => {
  const { activeOrganizationId, isPlatformAdmin } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const [uploadingOm, setUploadingOm] = useState(false);
  const [uploadingConops, setUploadingConops] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState({});
  const [versionHistories, setVersionHistories] = useState({});
  const [changeNote, setChangeNote] = useState("");
  const [showChangeNoteInput, setShowChangeNoteInput] = useState(null);
  const [selectedDocForCopilot, setSelectedDocForCopilot] = useState(null);
  
  const omFileRef = useRef(null);
  const conopsFileRef = useRef(null);
  const combinedFileRef = useRef(null);
  
  // Translations
  const t = language === 'cs' ? {
    title: "Generovat dokumenty",
    ready: "Připraveno",
    missingTemplate: "Chybí šablona",
    notReady: "Nepřipraveno",
    outOfScope: "Mimo rozsah SORA",
    outOfScopeExplanation: "Mise je mimo rozsah SORA Specific (Category C) — dokument by tvrdil neexistující SAIL úroveň, generování je proto zablokované.",
    generateConops: "ConOps",
    generateOm: "OM",
    generateUcl: "UCL balíček",
    uploadOm: "Nahrát OM šablonu",
    uploadConops: "Nahrát ConOps šablonu",
    uploadCombined: "Nahrát kombinovaný",
    generating: "Generuji...",
    progress1: "Analyzuji data mise...",
    progress2: "Konzultuji knowledge base...",
    progress3: "Generuji sekce dokumentu...",
    progress4: "Kontroluji SORA 2.5 shodu...",
    progress5: "Finalizuji dokument...",
    generated: "Vygenerováno",
    preview: "Náhled",
    downloadWord: "Word",
    downloadPdf: "PDF",
    regenerate: "Znovu",
    previousGenerations: "Předchozí generace",
    noGenerations: "Zatím žádné generace",
    templateNote: "Vlastní šablona nenahrána – použije se výchozí vzor",
    // Version-related translations
    version: "Verze",
    versionHistory: "Historie verzí",
    viewHistory: "Zobrazit historii",
    hideHistory: "Skrýt historii",
    changeNote: "Co se změnilo? (volitelné)",
    changeNotePlaceholder: "Např. aktualizovány bezpečnostní procedury...",
    newVersion: "Nová verze",
    generate: "Generovat",
    cancel: "Zrušit",
    noVersions: "Žádné předchozí verze",
    downloadVersion: "Stáhnout"
  } : {
    title: "Generate Documents",
    ready: "Ready",
    missingTemplate: "Missing template",
    notReady: "Not ready",
    outOfScope: "Out of SORA scope",
    outOfScopeExplanation: "The mission is outside SORA Specific scope (Category C) — a document would assert a nonexistent SAIL level, so generation is blocked.",
    generateConops: "ConOps",
    generateOm: "OM",
    generateUcl: "UCL Package",
    uploadOm: "Upload OM template",
    uploadConops: "Upload ConOps template",
    uploadCombined: "Upload combined",
    generating: "Generating...",
    progress1: "Analyzing mission data...",
    progress2: "Consulting knowledge base...",
    progress3: "Generating document sections...",
    progress4: "Checking SORA 2.5 compliance...",
    progress5: "Finalizing document...",
    generated: "Generated",
    preview: "Preview",
    downloadWord: "Word",
    downloadPdf: "PDF",
    regenerate: "Regenerate",
    previousGenerations: "Previous Generations",
    noGenerations: "No generations yet",
    templateNote: "No custom template – default will be used",
    // Version-related translations
    version: "Version",
    versionHistory: "Version History",
    viewHistory: "View history",
    hideHistory: "Hide history",
    changeNote: "What changed? (optional)",
    changeNotePlaceholder: "E.g., updated safety procedures...",
    newVersion: "New version",
    generate: "Generate",
    cancel: "Cancel",
    noVersions: "No previous versions",
    downloadVersion: "Download"
  };
  
  // Check readiness
  const readiness = useMemo(() => {
    const state = optimizationData?.current_state;
    const org = knowledgeBase?.organizational || [];

    const hasOmTemplate = org.some(d => d.doc_type === "om_template");
    const hasConopsTemplate = org.some(d => d.doc_type === "conops_template");
    const hasSoraAssessment = !!(state?.current_sail || mission?.sora_assessment?.sail_level);
    // Plan §3.9 point 6 / hardening #3: a mission whose final iGRC is out of Table 2/7's scope has no
    // real SAIL level to base a ConOps/OM/UCL document on — block generation entirely rather than let
    // a document silently assert "SAIL OUT_OF_SCOPE" as if it were a normal Specific-category result.
    const outOfScope = (state?.current_sail || "").toUpperCase() === "OUT_OF_SCOPE";

    return {
      canGenerate: hasSoraAssessment && !outOfScope,
      outOfScope,
      hasOmTemplate,
      hasConopsTemplate,
      templatesCount: (hasOmTemplate ? 1 : 0) + (hasConopsTemplate ? 1 : 0)
    };
  }, [optimizationData, knowledgeBase, mission]);
  
  // Extract mission context from props for pre-filling document generation
  const missionContext = useMemo(() => {
    const state = optimizationData?.current_state;
    if (!state && !mission) return null;
    
    // Build coordinates string
    let coordinates = null;
    if (mission?.latitude && mission?.longitude) {
      coordinates = `${mission.latitude}, ${mission.longitude}`;
    }
    
    return {
      // Basic mission info
      mission_name: mission?.name || mission?.mission_id || null,
      location: mission?.location || null,
      coordinates,
      mission_date: mission?.mission_date || null,
      
      // Aircraft from current SORA state (already resolved)
      aircraft_model: state?.drone_model || null,
      aircraft_mtom_kg: state?.mtom_kg || null,
      aircraft_ua_class: state?.ua_class || null,
      
      // Operation details
      operation_type: state?.operation_type || mission?.operation_type || null,
      environment_type: state?.environment || mission?.environment_type || null,
      max_altitude_m: state?.max_altitude_m || mission?.max_altitude_m || null,
      
      // SORA results (already calculated). Out-of-scope: never forward "GRC-OUT-OF-SCOPE"/
      // "OUT_OF_SCOPE" as if they were normal GRC/SAIL values (generation is blocked client-side —
      // see readiness.outOfScope — but this keeps the context itself honest too).
      sail_level: (state?.current_sail || "").toUpperCase() === "OUT_OF_SCOPE" ? null : (state?.current_sail || null),
      grc: state?.grc_out_of_scope ? null : (state?.current_grc || null),
      arc: state?.current_arc || null,
      sora_out_of_scope: !!state?.grc_out_of_scope,
      
      // Equipment status
      equipment: state?.current_equipment ? {
        parachute: state.current_equipment.parachute,
        fts: state.current_equipment.fts,
        geo_awareness: state.current_equipment.geo_awareness,
        remote_id: state.current_equipment.remote_id
      } : null,
      
      // Applied optimizations from mission history
      applied_optimizations: mission?.sora_assistant_changes || []
    };
  }, [mission, optimizationData]);
  
  // Get status badge
  const getStatusBadge = () => {
    if (readiness.outOfScope) {
      return <Badge variant="destructive" className="text-xs">{t.outOfScope}</Badge>;
    }
    if (!readiness.canGenerate) {
      return <Badge variant="destructive" className="text-xs">{t.notReady}</Badge>;
    }
    if (readiness.templatesCount < 2) {
      return <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">{t.missingTemplate}</Badge>;
    }
    return <Badge variant="outline" className="text-xs text-green-600 border-green-300">{t.ready}</Badge>;
  };
  
  // Upload template
  const uploadTemplate = async (file, docType) => {
    if (!file || !scopedOrganizationId) return;
    
    const setUploading = docType === "om_template" ? setUploadingOm : setUploadingConops;
    setUploading(true);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      await new Promise((resolve, reject) => {
        reader.onload = async () => {
          try {
            const base64 = reader.result.split(",")[1];
            await api.post(
              '/compliance/knowledge/upload',
              {
                organization_id: scopedOrganizationId,
                doc_type: docType,
                filename: file.name,
                description: docType === "om_template" 
                  ? (language === "cs" ? "Operations Manual šablona" : "Operations Manual template")
                  : (language === "cs" ? "ConOps šablona" : "ConOps template"),
                file_content_base64: base64
              }
            );
            toast.success(language === "cs" ? "Šablona nahrána" : "Template uploaded");
            resolve();
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : detail?.[language === "cs" ? "message_cs" : "message_en"];
      toast.error(message || (language === "cs" ? "Nahrání selhalo" : "Upload failed"));
    } finally {
      setUploading(false);
    }
  };
  
  // Fetch version history for a document type
  const fetchVersionHistory = useCallback(async (docType) => {
    try {
      const response = await api.get(`/documents/history/${missionId}/${docType}`);
      setVersionHistories(prev => ({
        ...prev,
        [docType]: response.data.versions || []
      }));
    } catch (err) {
      console.error("Failed to fetch version history:", err);
    }
  }, [missionId]);
  
  // Toggle version history visibility
  const toggleVersionHistory = (docType) => {
    const newState = !showVersionHistory[docType];
    setShowVersionHistory(prev => ({ ...prev, [docType]: newState }));
    if (newState && !versionHistories[docType]) {
      fetchVersionHistory(docType);
    }
  };
  
  // Download a specific version
  const downloadVersion = async (generationId, format = "docx") => {
    try {
      const response = await api.get(
        `/compliance/download/${generationId}?format=${format}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `document_${generationId.substring(0, 8)}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(language === "cs" ? "Stažení selhalo" : "Download failed");
    }
  };

  // Generate document with optional change note
  const generateDocument = useCallback(async (docType, noteForGeneration = "") => {
    // 2026-09-16 review (Codex gpt-5.6-sol): the main "Generate" buttons check
    // readiness.canGenerate via `disabled`, but "Regenerate" (and the dialog behind it) call this
    // function directly without that check — the single choke point for every caller is here, not
    // scattered across each button.
    if (readiness.outOfScope) {
      toast.error(t.outOfScopeExplanation);
      return;
    }
    setGeneratingDoc(docType);
    setGeneratedDoc(null);
    setProgressStep(0);
    setShowChangeNoteInput(null);
    setChangeNote("");
    
    let pollInterval = null;
    
    try {
      let endpoint = "";
      if (docType === "conops") endpoint = `/compliance/generate/conops/${missionId}`;
      else if (docType === "om") endpoint = `/compliance/generate/om/${missionId}`;
      else if (docType === "ucl") endpoint = `/compliance/generate/ucl-package/${missionId}`;
      
      // Build payload with mission context for pre-filling
      const payload = { 
        language,
        change_note: noteForGeneration || null,
        mission_context: missionContext  // Pass pre-filled mission data
      };
      
      const startResponse = await api.post(endpoint, payload);
      const generationId = startResponse.data.generation_id;
      const newVersion = startResponse.data.version;
      
      if (!generationId) {
        setGeneratedDoc(startResponse.data);
        toast.success(language === "cs" ? "Dokument vygenerován" : "Document generated");
        setGeneratingDoc(null);
        onRefreshGenerations?.();
        // Refresh version history
        fetchVersionHistory(docType);
        return;
      }
      
      const pollForStatus = async () => {
        try {
          const statusResponse = await api.get(`/compliance/generation/${generationId}`);
          
          const { status, progress, error_message, version } = statusResponse.data;
          
          if (status === "pending") setProgressStep(1);
          else if (status === "generating") {
            if (progress?.includes("mission")) setProgressStep(1);
            else if (progress?.includes("SORA")) setProgressStep(2);
            else if (progress?.includes("knowledge") || progress?.includes("context")) setProgressStep(3);
            else if (progress?.includes("AI") || progress?.includes("Generating")) setProgressStep(4);
            else if (progress?.includes("Auto-fill")) setProgressStep(5);
            else setProgressStep(3);
          }
          
          if (status === "completed") {
            if (pollInterval) clearInterval(pollInterval);
            setGeneratedDoc({ ...statusResponse.data, version: version || newVersion });
            toast.success(
              language === "cs" 
                ? `Dokument vygenerován (v${version || newVersion})` 
                : `Document generated (v${version || newVersion})`
            );
            setGeneratingDoc(null);
            setProgressStep(0);
            onRefreshGenerations?.();
            // Refresh version history
            fetchVersionHistory(docType);
          } else if (status === "failed") {
            if (pollInterval) clearInterval(pollInterval);
            toast.error(error_message || (language === "cs" ? "Generování selhalo" : "Generation failed"));
            setGeneratingDoc(null);
            setProgressStep(0);
          }
        } catch (pollErr) {
          console.error("Polling error:", pollErr);
        }
      };
      
      pollInterval = setInterval(pollForStatus, 3000);
      await pollForStatus();
      
    } catch (err) {
      if (pollInterval) clearInterval(pollInterval);
      toast.error(err.response?.data?.detail || (language === "cs" ? "Generování selhalo" : "Generation failed"));
      setGeneratingDoc(null);
      setProgressStep(0);
    }
  }, [missionId, language, setGeneratingDoc, setGeneratedDoc, onRefreshGenerations, missionContext, fetchVersionHistory, readiness.outOfScope, t.outOfScopeExplanation]);
  
  // Download document
  const downloadDocument = async (generationId, genType, format) => {
    try {
      const url = `/compliance/generation/${generationId}/${format}?lang=${language}`;
      const response = await api.get(url, { responseType: 'blob' });
      
      const mimeType = format === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      
      const blob = new Blob([response.data], { type: mimeType });
      if (blob.size === 0) {
        toast.error(language === "cs" ? "Prázdný soubor" : "Empty file");
        return;
      }
      
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${genType || "document"}_${missionId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success(language === "cs" ? "Staženo" : "Downloaded");
    } catch (err) {
      toast.error(language === "cs" ? "Stažení selhalo" : "Download failed");
    }
  };
  
  // Progress messages
  const progressMessages = [t.progress1, t.progress2, t.progress3, t.progress4, t.progress5];
  
  // Completed generations
  const completedGenerations = previousGenerations.filter(g => g.status === "completed" && g.has_content);

  return (
    <div className="p-4 rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] shadow-sm" data-testid="document-generation-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#1E3A5F]" />
          <span className="font-medium text-[var(--text-primary)]">{t.title}</span>
        </div>
        {getStatusBadge()}
      </div>

      {readiness.outOfScope && (
        <div className="mb-4 p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 flex items-start gap-2" data-testid="document-generation-out-of-scope">
          <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-rose-700 dark:text-rose-300">{t.outOfScopeExplanation}</p>
        </div>
      )}

      {/* Generation Progress */}
      {generatingDoc && (
        <div className="mb-4 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">{t.generating}</span>
          </div>
          <div className="space-y-1">
            {progressMessages.map((msg, idx) => (
              <div key={idx} className={`flex items-center gap-2 text-xs ${
                idx === progressStep ? "text-purple-700 dark:text-purple-300 font-medium" 
                : idx < progressStep ? "text-green-600 dark:text-green-400" 
                : "text-purple-400 dark:text-purple-500"
              }`}>
                {idx < progressStep ? <CheckCircle2 className="h-3 w-3" /> 
                : idx === progressStep ? <Loader2 className="h-3 w-3 animate-spin" /> 
                : <span className="h-3 w-3 rounded-full border border-current" />}
                {msg}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Generated Document Result */}
      {generatedDoc && !generatingDoc && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-700 dark:text-green-300">{t.generated}</span>
            {generatedDoc.version && (
              <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                v{generatedDoc.version}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onShowPreview} className="h-7 text-xs gap-1">
              <Eye className="h-3 w-3" /> {t.preview}
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadDocument(generatedDoc.generation_id, generatedDoc.generation_type, "docx")} className="h-7 text-xs gap-1">
              <Download className="h-3 w-3" /> {t.downloadWord}
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadDocument(generatedDoc.generation_id, generatedDoc.generation_type, "pdf")} className="h-7 text-xs gap-1">
              <Download className="h-3 w-3" /> {t.downloadPdf}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowChangeNoteInput(generatedDoc.generation_type)} className="h-7 text-xs gap-1" disabled={readiness.outOfScope}>
              <RefreshCw className="h-3 w-3" /> {t.regenerate}
            </Button>
          </div>
        </div>
      )}
      
      {/* Change Note Input Dialog */}
      {showChangeNoteInput && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-2">
            <History className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{t.newVersion}</span>
          </div>
          <div className="space-y-2">
            <input
              type="text"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder={t.changeNotePlaceholder}
              className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="change-note-input"
            />
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={() => generateDocument(showChangeNoteInput, changeNote)}
                className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700"
              >
                <FileText className="h-3 w-3" /> {t.generate}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setShowChangeNoteInput(null); setChangeNote(""); }}
                className="h-7 text-xs"
              >
                {t.cancel}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Generate Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowChangeNoteInput("conops")}
          disabled={!readiness.canGenerate || !!generatingDoc}
          className="gap-1"
        >
          {generatingDoc === "conops" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          {generatingDoc === "conops" ? t.generating : t.generateConops}
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowChangeNoteInput("om")}
          disabled={!readiness.canGenerate || !!generatingDoc}
          className="gap-1"
        >
          {generatingDoc === "om" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          {generatingDoc === "om" ? t.generating : t.generateOm}
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowChangeNoteInput("ucl")}
          disabled={!readiness.canGenerate || !!generatingDoc}
          className="gap-1"
        >
          {generatingDoc === "ucl" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          {generatingDoc === "ucl" ? t.generating : t.generateUcl}
        </Button>
      </div>
      
      {/* Template Upload */}
      <div className="flex flex-wrap gap-2 mb-3">
        <input type="file" ref={omFileRef} className="hidden" accept=".pdf,.docx" 
          onChange={(e) => e.target.files?.[0] && uploadTemplate(e.target.files[0], "om_template")} />
        <input type="file" ref={conopsFileRef} className="hidden" accept=".pdf,.docx" 
          onChange={(e) => e.target.files?.[0] && uploadTemplate(e.target.files[0], "conops_template")} />
        
        <Button variant="ghost" size="sm" onClick={() => omFileRef.current?.click()} disabled={uploadingOm} className="h-7 text-xs gap-1">
          {uploadingOm ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          {t.uploadOm}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => conopsFileRef.current?.click()} disabled={uploadingConops} className="h-7 text-xs gap-1">
          {uploadingConops ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          {t.uploadConops}
        </Button>
      </div>
      
      {(!readiness.hasOmTemplate || !readiness.hasConopsTemplate) && (
        <p className="text-xs text-[var(--text-muted)] mb-3 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-amber-500" />
          {t.templateNote}
        </p>
      )}
      
      {/* Previous Generations */}
      {completedGenerations.length > 0 && (
        <Collapsible open={showHistory} onOpenChange={setShowHistory}>
          <CollapsibleTrigger className="flex items-center gap-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] w-full">
            <History className="h-3 w-3" />
            {t.previousGenerations} ({completedGenerations.length})
            {showHistory ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-2">
            {completedGenerations.slice(0, 5).map((gen) => {
              const typeLabels = { conops: "ConOps", om: "OM", ucl_package: "UCL" };
              const label = typeLabels[gen.generation_type] || gen.generation_type;
              const dateStr = gen.created_at ? new Date(gen.created_at).toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US") : "";
              const version = gen.version || "1.0";
              const isSelected = selectedDocForCopilot?.generation_id === gen.generation_id;
              
              return (
                <div key={gen.generation_id}>
                  <div className={`flex items-center justify-between p-2 rounded text-xs ${isSelected ? "bg-[#1E3A5F]/10 border border-[#1E3A5F]/30" : "bg-[var(--bg-elevated)]"}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[var(--text-secondary)]">{label}</span>
                      <Badge variant="outline" className="text-[10px] h-4 px-1">v{version}</Badge>
                      <span className="text-[var(--text-muted)]">· {dateStr}</span>
                      {gen.change_note && (
                        <span className="text-[var(--text-muted)] italic truncate max-w-[150px]" title={gen.change_note}>
                          — {gen.change_note}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant={isSelected ? "default" : "ghost"} 
                        size="sm" 
                        onClick={() => setSelectedDocForCopilot(isSelected ? null : gen)} 
                        className={`h-6 px-2 text-xs gap-1 ${isSelected ? "bg-[#1E3A5F] text-white" : ""}`}
                        data-testid={`edit-ai-btn-${gen.generation_id}`}
                      >
                        {language === 'cs' ? 'AI úpravy' : 'AI Edit'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => downloadDocument(gen.generation_id, gen.generation_type, "pdf")} className="h-6 px-2 text-xs">
                        PDF
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => downloadDocument(gen.generation_id, gen.generation_type, "docx")} className="h-6 px-2 text-xs">
                        Word
                      </Button>
                    </div>
                  </div>
                  
                  {/* AI Copilot Panel for this document */}
                  {isSelected && (
                    <div className="mt-2 ml-4 border-l-2 border-[#1E3A5F]/30 pl-3">
                      <DocumentCopilotChat
                        documentId={gen.generation_id}
                        documentContent={gen.content}
                        onDocumentUpdate={(newContent) => {
                          // Refresh generations to get updated content
                          if (onRefreshGenerations) onRefreshGenerations();
                        }}
                        language={language}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      )}
      
      {/* Version History Panels */}
      {["conops", "om", "ucl"].map(docType => {
        const history = versionHistories[docType] || [];
        const isVisible = showVersionHistory[docType];
        
        if (!isVisible && history.length === 0) return null;
        
        return (
          <div key={docType} className="mt-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => toggleVersionHistory(docType)}
              className="h-7 text-xs gap-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <History className="h-3 w-3" />
              {docType.toUpperCase()} {t.versionHistory}
              <ChevronDown className={`h-3 w-3 transition-transform ${isVisible ? "rotate-180" : ""}`} />
            </Button>
            
            {isVisible && (
              <div className="mt-2 space-y-1 pl-4 border-l-2 border-[var(--border-default)]">
                {history.length === 0 ? (
                  <p className="text-xs text-[var(--text-muted)] py-2">{t.noVersions}</p>
                ) : (
                  history.map((ver, idx) => (
                    <div key={ver.id} className="flex items-center justify-between p-2 rounded bg-[var(--bg-elevated)] text-xs">
                      <div className="flex items-center gap-2">
                        <Badge variant={idx === 0 ? "default" : "outline"} className="text-[10px] h-4 px-1">
                          v{ver.version}
                        </Badge>
                        <span className="text-[var(--text-muted)]">
                          {new Date(ver.created_at).toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US")}
                        </span>
                        {ver.change_note && (
                          <span className="text-[var(--text-secondary)] truncate max-w-[200px]" title={ver.change_note}>
                            {ver.change_note}
                          </span>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => downloadVersion(ver.id, "docx")}
                        className="h-6 px-2 text-xs"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        {t.downloadVersion}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DocumentGenerationCard;
